import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Notification } from '../notification.model';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faSlidersH, faEye } from '@fortawesome/free-solid-svg-icons';
import { H1Heading } from '../../h1-heading/h1-heading';
import { NotificationService } from '../../../services/notifications/notification.service';
import { ContentSkeleton } from '../../content-skeleton/content-skeleton';

@Component({
  selector: 'app-notifications-list',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, H1Heading, ContentSkeleton],
  templateUrl: './notifications-list.html',
  styleUrls: ['./notifications-list.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsListComponent implements OnInit, OnChanges {
  constructor(private _eref: ElementRef, private cdr: ChangeDetectorRef) {}
  private notificationService = inject(NotificationService);

  @Input() allNotifications: Notification[] = [];
  @Input() isLoading: boolean = false;
  @Input() currentPage: number = 1;
  @Input() totalPages: number = 1;
  @Input() total: number = 0; // Total count from API
  @Input() unreadCount: number = 0; // Unread count from API
  @Input() alertsCount: number = 0; // Alerts count from API
  @Input() messagesCount: number = 0; // Messages count from API
  @Input() itemsPerPage: number = 5; // Items per page from parent
  @Input() activeTab: 'All' | 'Alerts' | 'Messages' = 'All';
  @Output() back = new EventEmitter<void>();
  @Output() notificationSelected = new EventEmitter<Notification>();
  @Output() notificationDeleted = new EventEmitter<number>();
  @Output() notificationMarkedRead = new EventEmitter<number>();
  @Output() notificationsUpdated = new EventEmitter<void>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() tabChange = new EventEmitter<'All' | 'Alerts' | 'Messages'>();

  selectNotification(note: Notification) {
    this.notificationSelected.emit(note);
  }
  faSlidersH = faSlidersH;
  faEye = faEye;

  // Store notifications (already paginated from backend)
  pagedNotifications: Notification[] = [];

  // Filter popup controls
  filterPopupOpen: boolean = false;
  filterUnread: boolean = true;
  filterRead: boolean = false;
  filterAll: boolean = false;

  // Delete confirmation popup
  deleteConfirmId: number | null = null;

  // Use input notifications, fallback to empty array
  get notifications(): Notification[] {
    return this.allNotifications && this.allNotifications.length > 0
      ? this.allNotifications
      : [];
  }

  ngOnInit() {
    this.updateDisplay();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['allNotifications'] || changes['activeTab'] || changes['alertsCount'] || changes['messagesCount'] || changes['unreadCount']) {
      this.updateDisplay();
      this.cdr.markForCheck();
    }
  }

  updateDisplay() {
    // Notifications are already filtered and paginated from backend
    this.pagedNotifications = this.notifications;
  }

  // Filter popup logic
  toggleFilterPopup(event: MouseEvent) {
    event.stopPropagation();
    this.filterPopupOpen = true;
  }

  @HostListener('document:click', ['$event'])
  closePopupOnOutsideClick(event: MouseEvent) {
    if (this.filterPopupOpen) this.filterPopupOpen = false;
  }
  changeTab(tab: 'All' | 'Alerts' | 'Messages') {
    this.tabChange.emit(tab);
    this.cdr.markForCheck();
  }

  get allCount(): number {
    // Must match Alerts + Messages; API unread_count can disagree with tab filter rules.
    return this.alertsCount + this.messagesCount;
  }

  get alertCount(): number {
    // Use alerts count from API response
    return this.alertsCount;
  }

  get messageCount(): number {
    // Use messages count from API response
    return this.messagesCount;
  }

  get hasUnreadNotifications() {
    return this.notifications.some((n) => n.status === 'unread');
  }

  applyFilter() {
    // Example filter logic; extend as needed
    if (this.filterAll) {
      this.pagedNotifications = this.notifications;
    } else if (this.filterUnread && this.filterRead) {
      this.pagedNotifications = this.notifications.filter(
        (n) => n.status === 'unread' || n.status === 'read',
      );
    } else if (this.filterUnread) {
      this.pagedNotifications = this.notifications.filter((n) => n.status === 'unread');
    } else if (this.filterRead) {
      this.pagedNotifications = this.notifications.filter((n) => n.status === 'read');
    } else {
      this.pagedNotifications = [];
    }
    this.filterPopupOpen = false; // Optionally close after applying
  }

  markAsRead(id: number, event?: MouseEvent) {
    event?.stopPropagation();
    this.notificationMarkedRead.emit(id);
  }

  openDeleteConfirm(id: number, event: MouseEvent) {
    event.stopPropagation();
    this.deleteConfirmId = id;
  }

  closeDeleteConfirm() {
    this.deleteConfirmId = null;
  }

  confirmDelete() {
    if (this.deleteConfirmId !== null) {
      this.deleteNotification(this.deleteConfirmId);
      this.closeDeleteConfirm();
    }
  }

  deleteNotification(id: number) {
    // Parent deletes via API and reloads the current page so the list stays full (no gap).
    this.notificationDeleted.emit(id);
  }

  markAllAsRead() {
    this.notificationService.markAllAsRead().subscribe({
      next: () => {
        this.notifications.forEach((n) => {
          n.status = 'read';
          n.is_read = true;
        });
        this.updateDisplay();
        this.notificationsUpdated.emit();
        this.cdr.markForCheck();
      },
      error: (err: unknown) => {
        console.error('Error marking all notifications as read:', err);
        this.cdr.markForCheck();
      },
    });
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.pageChange.emit(this.currentPage + 1);
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.pageChange.emit(this.currentPage - 1);
    }
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.pageChange.emit(page);
    }
  }

  getStartIndex(): number {
    if (this.total === 0) return 0;
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  getEndIndex(): number {
    if (this.total === 0) return 0;
    return Math.min(this.currentPage * this.itemsPerPage, this.total);
  }

  get totalCount(): number {
    return this.total;
  }

  get hasNext(): boolean {
    return this.currentPage < this.totalPages;
  }

  get hasPrevious(): boolean {
    return this.currentPage > 1;
  }

  get shouldShowPagination(): boolean {
    return this.totalPages > 1;
  }
}
